import { types } from "mobx-state-tree";
import { flow } from "modules/common/models/flow";
import { Notificator } from "modules/common/models/notificator";
import { Transport } from "modules/common/models/transport";
import { apiUrls } from "modules/common/services/communication/urls";
import { OrgChangeResult, OrgLogRecord, OrgStaffSlot, OrgTreeData, OrgUnitData, OrgUnitTypeData } from "./types";

/**
 * Стор дерева структуры организации.
 *
 * Данные дерева — один замороженный снимок с сервера: дерево читается целиком и
 * перестраивается в раскладку на клиенте, отдельные MST-узлы под каждое подразделение
 * не нужны. Правки — оптимистичные: снимок меняется сразу (анимация перестройки
 * идёт без ожидания сервера), затем уходит запрос; ошибка возвращает прежний снимок.
 * После успешной правки снимок перечитывается тихо — сервер мог снять руководителей
 * или поправить порядок. Каждая правка кладёт в стек обратное действие для «Отменить».
 */

export type SaveStatus = "idle" | "saving" | "saved" | "error";

export interface UnitPatch {
    id: string;
    name: string;
    description: string;
    order: number | null;
    parentId: string | null;
    unitTypeId: string | null;
    managerId: string | null;
}

/** Обратное действие для «Отменить» */
export type UndoAction =
    | { kind: "save"; label: string; patch: UnitPatch }
    | { kind: "delete"; label: string; id: string }
    /** штат — чтобы отмена удаления вернула подразделение вместе с ним */
    | { kind: "create"; label: string; patch: UnitPatch; staff?: OrgStaffSlot[] }
    | { kind: "move"; label: string; id: string; parentId: string | null; index: number }
    | { kind: "employee"; label: string; employerId: string; departmentId: string }
    | { kind: "staff"; label: string; unitId: string; staff: OrgStaffSlot[] };

const UNDO_DEPTH = 20;

export const OrgTreeStore = types
    .compose(
        Transport,
        Notificator,
        types.model({
            data: types.maybeNull(types.frozen<OrgTreeData>()),
            loading: false,
            status: types.optional(types.enumeration<SaveStatus>(["idle", "saving", "saved", "error"]), "idle"),
            undoStack: types.optional(types.frozen<UndoAction[]>(), []),
            log: types.optional(types.frozen<OrgLogRecord[]>(), []),
            logHasMore: false,
            logLoading: false,
        })
    )
    .volatile(() => ({
        pending: 0,
        statusTimer: 0 as unknown as ReturnType<typeof setTimeout>,
    }))
    .views((self) => ({
        get canUndo() {
            return self.undoStack.length > 0 && self.pending === 0;
        },
        get lastUndoLabel() {
            return self.undoStack.length ? self.undoStack[self.undoStack.length - 1].label : "";
        },
        unitById(id: string) {
            return self.data?.units.find((u) => u.id === id) ?? null;
        },
    }))
    .actions((self) => {
        const setStatus = (status: SaveStatus) => {
            self.status = status;
            clearTimeout(self.statusTimer);
            if (status === "saved") {
                // «Сохранено» гаснет само; таймер зовёт действие стора, а не меняет поле напрямую
                self.statusTimer = setTimeout(() => (self as any).setStatus("idle"), 2500);
            }
        };

        return {
            setData(data: OrgTreeData) {
                self.data = data;
            },

            setStatus,

            pushUndo(action: UndoAction) {
                self.undoStack = [...self.undoStack, action].slice(-UNDO_DEPTH);
            },

            popUndo() {
                const stack = [...self.undoStack];
                const action = stack.pop();
                self.undoStack = stack;
                return action;
            },

            begin() {
                self.pending++;
                setStatus("saving");
            },

            end(ok: boolean) {
                self.pending = Math.max(0, self.pending - 1);
                if (!ok) {
                    setStatus("error");
                } else if (self.pending === 0) {
                    setStatus("saved");
                }
            },

            /** Поменять снимок локально (оптимистично) */
            mutate(change: (data: OrgTreeData) => OrgTreeData) {
                if (self.data) {
                    self.data = change(self.data);
                }
            },
        };
    })
    .actions((self) => {
        const showWarnings = (result: OrgChangeResult | null | undefined) => {
            (result?.warnings ?? []).forEach((w) => self.notify.warning(w));
        };

        /**
         * Общий каркас правки: локальное изменение → запрос → тихое перечитывание.
         * Ошибка — вернуть снимок и показать причину.
         */
        const run = flow(function* (
            apply: ((data: OrgTreeData) => OrgTreeData) | null,
            request: () => Promise<any>,
            undo: UndoAction | null,
            /** тост об успехе; у служебных вызовов (отмена действия) его нет */
            success: string | null = null
        ) {
            const before = self.data;
            if (apply) {
                self.mutate(apply);
            }
            self.begin();

            try {
                const result: any = yield request();
                if (undo) {
                    self.pushUndo(undo);
                }
                self.end(true);
                if (success) {
                    self.notify.success(success);
                }
                showWarnings(result as any);
                // тихо: без индикатора загрузки
                reload();
                return result ?? true;
            } catch (er) {
                if (before) {
                    self.setData(before);
                }
                self.end(false);
                self.notify.error(er);
                return null;
            }
        });

        const reload = flow(function* () {
            try {
                const data: OrgTreeData = yield self.transport.get<OrgTreeData>(apiUrls.orgStructure.tree());
                // пока шёл запрос, могла начаться новая правка — её локальный снимок не затираем
                if (self.pending === 0) {
                    self.setData(data);
                }
            } catch {
                // тихое перечитывание: ошибку покажет следующая явная загрузка
            }
        });

        const toPatch = (u: OrgUnitData): UnitPatch => ({
            id: u.id,
            name: u.name,
            description: u.description,
            order: u.order,
            parentId: u.parentId,
            unitTypeId: u.unitTypeId,
            managerId: u.managerId,
        });

        const siblingsOf = (data: OrgTreeData, parentId: string | null, exceptId?: string) =>
            data.units
                .filter((u) => (u.parentId ?? null) === parentId && u.id !== exceptId)
                .sort(
                    (a, b) =>
                        (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER) ||
                        a.name.localeCompare(b.name, "ru")
                );

        return {
            load: flow(function* () {
                self.loading = true;
                try {
                    const data: OrgTreeData = yield self.transport.get<OrgTreeData>(apiUrls.orgStructure.tree());
                    self.setData(data);
                    return true;
                } catch (er) {
                    self.notify.error(er);
                    return false;
                } finally {
                    self.loading = false;
                }
            }),

            reload,

            /** Создать подразделение. Возвращает созданное */
            createUnit: flow(function* (patch: Omit<UnitPatch, "id">, withUndo = true) {
                const created: OrgUnitData | null = yield run(
                    null,
                    () => self.transport.post<OrgUnitData>(apiUrls.orgStructure.saveUnit(), { ...patch, id: "" }),
                    null,
                    withUndo ? "Подразделение добавлено" : null
                );
                if (created) {
                    self.mutate((d) => ({ ...d, units: [...d.units.filter((u) => u.id !== created.id), created] }));
                    if (withUndo) {
                        self.pushUndo({ kind: "delete", label: `создание «${created.name}»`, id: created.id });
                    }
                }
                return created;
            }),

            /** Изменить поля подразделения (название, тип, руководитель, примечания, №) */
            updateUnit: flow(function* (patch: UnitPatch, label = "изменение подразделения", withUndo = true) {
                const current = self.unitById(patch.id);
                if (!current) {
                    return null;
                }
                const result: OrgUnitData | null = yield run(
                    (d) => ({ ...d, units: d.units.map((u) => (u.id === patch.id ? { ...u, ...patch } : u)) }),
                    () => self.transport.post<OrgUnitData>(apiUrls.orgStructure.saveUnit(), patch),
                    withUndo ? { kind: "save", label, patch: toPatch(current) } : null,
                    withUndo ? "Изменения сохранены" : null
                );
                return result;
            }),

            /** Заменить штатное расписание подразделения */
            saveStaff: flow(function* (unitId: string, staff: OrgStaffSlot[], withUndo = true) {
                const current = self.unitById(unitId);
                if (!current) {
                    return null;
                }
                return yield run(
                    (d) => ({ ...d, units: d.units.map((u) => (u.id === unitId ? { ...u, staff } : u)) }),
                    () =>
                        self.transport.post<OrgUnitData>(apiUrls.orgStructure.saveStaff(), {
                            unitId,
                            staff: staff.map(({ positionId, gradeGuid, count }) => ({ positionId, gradeGuid, count })),
                        }),
                    withUndo ? { kind: "staff", label: "изменение штата", unitId, staff: current.staff ?? [] } : null,
                    withUndo ? "Штат сохранён" : null
                );
            }),

            /** Перенести узел (с веткой) к новому родителю на позицию index среди соседей */
            moveUnit: flow(function* (id: string, parentId: string | null, index: number, withUndo = true) {
                const data = self.data;
                const current = self.unitById(id);
                if (!data || !current) {
                    return null;
                }
                const oldParent = current.parentId ?? null;
                const oldIndex = siblingsOf(data, oldParent).findIndex((u) => u.id === id);

                const apply = (d: OrgTreeData): OrgTreeData => {
                    const siblings = siblingsOf(d, parentId, id);
                    const moved = { ...current, parentId };
                    siblings.splice(Math.max(0, Math.min(index, siblings.length)), 0, moved);
                    const orders = new Map(siblings.map((u, i) => [u.id, i + 1]));
                    return {
                        ...d,
                        units: d.units.map((u) =>
                            u.id === id
                                ? { ...moved, order: orders.get(id) ?? null }
                                : orders.has(u.id)
                                  ? { ...u, order: orders.get(u.id)! }
                                  : u
                        ),
                    };
                };

                return yield run(
                    apply,
                    () =>
                        self.transport.post<OrgChangeResult>(apiUrls.orgStructure.moveUnit(), { id, parentId, index }),
                    withUndo
                        ? { kind: "move", label: `перенос «${current.name}»`, id, parentId: oldParent, index: oldIndex }
                        : null,
                    withUndo ? `«${current.name}» перенесено` : null
                );
            }),

            deleteUnit: flow(function* (id: string, withUndo = true) {
                const current = self.unitById(id);
                if (!current) {
                    return null;
                }
                return yield run(
                    (d) => ({ ...d, units: d.units.filter((u) => u.id !== id) }),
                    () => self.transport.delete<boolean>(apiUrls.orgStructure.deleteUnit(id)),
                    withUndo
                        ? {
                              kind: "create",
                              label: `удаление «${current.name}»`,
                              patch: toPatch(current),
                              staff: current.staff,
                          }
                        : null,
                    withUndo ? `«${current.name}» удалено` : null
                );
            }),

            /** Перевести сотрудника в другое подразделение без смены должности */
            /** Отменить перенос (Ctrl+Z): отмена последнего перевода сотрудника */
            revertEmployeeMove: flow(function* (employerId: string, departmentId: string) {
                return yield run(
                    (d) => ({
                        ...d,
                        employees: d.employees.map((e) => (e.id === employerId ? { ...e, departmentId } : e)),
                    }),
                    () =>
                        self.transport.post<OrgChangeResult>(apiUrls.orgStructure.revertEmployeeMove(), { employerId }),
                    null
                );
            }),

            /** Условия оплаты сотрудника (с правом на штатку) - подставить в перенос */
            loadConditions: flow(function* (employerId: string) {
                try {
                    const rows: any[] = yield self.transport.get<any>(apiUrls.schedule.compensation(employerId));
                    return rows ?? [];
                } catch (er) {
                    return [];
                }
            }),

            /** Справочник должностей - для переноса со сменой должности */
            loadPositions: flow(function* () {
                try {
                    const items: { id: string; label: string }[] = yield self.transport.get<any>(
                        apiUrls.employeePositions.dictionary()
                    );
                    return items.map((i) => ({ id: i.id, label: i.label }));
                } catch (er) {
                    return [];
                }
            }),

            /** Перевод в подразделение с даты (можно сразу на другую должность); выплаты не меняются */
            moveEmployee: flow(function* (
                employerId: string,
                departmentId: string,
                date: string,
                positionId: string | null = null,
                pay: { salary: number; bonus: number } | null = null,
                withUndo = true
            ) {
                const employee = self.data?.employees.find((e) => e.id === employerId);
                if (!employee || (employee.departmentId === departmentId && !positionId)) {
                    return null;
                }
                return yield run(
                    (d) => ({
                        ...d,
                        employees: d.employees.map((e) => (e.id === employerId ? { ...e, departmentId } : e)),
                    }),
                    () =>
                        self.transport.post<OrgChangeResult>(apiUrls.orgStructure.moveEmployee(), {
                            employerId,
                            departmentId,
                            date,
                            positionId,
                            salary: pay?.salary ?? null,
                            bonus: pay?.bonus ?? null,
                        }),
                    withUndo
                        ? {
                              kind: "employee",
                              label: `перевод ${employee.name}`,
                              employerId,
                              departmentId: employee.departmentId,
                          }
                        : null,
                    withUndo ? `${employee.name}: перевод сохранён` : null
                );
            }),

            saveType: flow(function* (type: Partial<OrgUnitTypeData>) {
                return yield run(
                    null,
                    () => self.transport.post<OrgUnitTypeData>(apiUrls.orgStructure.saveType(), type),
                    null,
                    "Тип сохранён"
                );
            }),

            deleteType: flow(function* (id: string) {
                return yield run(
                    null,
                    () => self.transport.delete<boolean>(apiUrls.orgStructure.deleteType(id)),
                    null,
                    "Тип удалён"
                );
            }),

            loadLog: flow(function* (more = false) {
                self.logLoading = true;
                try {
                    const before = more && self.log.length ? self.log[self.log.length - 1].id : undefined;
                    const page: { items: OrgLogRecord[]; hasMore: boolean } = yield self.transport.get(
                        apiUrls.orgStructure.log(before)
                    );
                    self.log = more ? [...self.log, ...page.items] : page.items;
                    self.logHasMore = page.hasMore;
                } catch (er) {
                    self.notify.error(er);
                } finally {
                    self.logLoading = false;
                }
            }),

            loadManaged: flow(function* (employerId: string) {
                try {
                    const units: { id: string; name: string }[] = yield self.transport.get(
                        apiUrls.orgStructure.managed(employerId)
                    );
                    return units;
                } catch {
                    return [];
                }
            }),
        };
    })
    .actions((store) => {
        // без явного типа TS упирается в глубину вывода типов MST на замороженных данных
        const self = store as unknown as UndoTarget;
        return {
            /** Отменить последнее действие */
            undo: flow(function* () {
                const action = self.popUndo();
                if (!action) {
                    return;
                }
                let ok: any = null;
                switch (action.kind) {
                    case "save":
                        ok = yield self.updateUnit(action.patch, "", false);
                        break;
                    case "delete":
                        ok = yield self.deleteUnit(action.id, false);
                        break;
                    case "create": {
                        const { id, ...rest } = action.patch;
                        const created = (yield self.createUnit(rest, false)) as OrgUnitData | null;
                        ok = created;
                        if (created && action.staff?.length) {
                            ok = yield self.saveStaff(created.id, action.staff, false);
                        }
                        break;
                    }
                    case "move":
                        ok = yield self.moveUnit(action.id, action.parentId, action.index, false);
                        break;
                    case "staff":
                        ok = yield self.saveStaff(action.unitId, action.staff, false);
                        break;
                    case "employee":
                        // отмена переноса - отмена самого перевода, а не ещё один перевод назад
                        ok = yield self.revertEmployeeMove(action.employerId, action.departmentId);
                        break;
                }
                if (ok) {
                    self.notify.info(`Отменено: ${action.label}`);
                }
            }),
        };
    })
    .named("OrgTreeStore");

interface UndoTarget {
    popUndo(): UndoAction | undefined;
    updateUnit(patch: UnitPatch, label: string, withUndo: boolean): Promise<unknown>;
    saveStaff(unitId: string, staff: OrgStaffSlot[], withUndo: boolean): Promise<unknown>;
    deleteUnit(id: string, withUndo: boolean): Promise<unknown>;
    createUnit(patch: Omit<UnitPatch, "id">, withUndo: boolean): Promise<unknown>;
    moveUnit(id: string, parentId: string | null, index: number, withUndo: boolean): Promise<unknown>;
    revertEmployeeMove(employerId: string, departmentId: string): Promise<unknown>;
    notify: { info(message: string): void };
}

export type OrgTreeStoreType = typeof OrgTreeStore.Type;

export const initialState = (): typeof OrgTreeStore.SnapshotType => ({
    data: null,
    loading: false,
    status: "idle",
    undoStack: [],
    log: [],
    logHasMore: false,
    logLoading: false,
});
